import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import UploadIcon from "../../assets/images/Svg/UploadIcon";
import AddIcon from "@mui/icons-material/Add";
import ThreeSixtyIcon from "@mui/icons-material/ThreeSixty";
import { colors } from "../../theme/colors";
import { fonts } from "../../theme/fonts";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "../../context/Internationalization";
import { validateImageFile } from "./validators";
import { Box, Button } from "@mui/material";
import UploadFilePreview from "./UploadFilePreview";

const { Primary, Grey, Error } = colors;
const { text } = fonts;

const useStyles = makeStyles((_theme) => ({
  label: {
    ...text.sm.medium,
    color: Grey[700],
  },
  highlightText: {
    ...text.sm.normal,
    color: Primary[700],
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  errorText: {
    ...text.sm.normal,
    color: Error[500],
  },
  text: {
    ...text.sm.normal,
    color: Grey[500],
  },
  previewText: {
    ...text.sm.normal,
    color: Grey[500],
    textAlign: "center",
    display: "block",
  },
  uploadFileContainer: {
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflowWrap: "anywhere",
    border: `1px dashed ${Grey[300]}`,
    borderRadius: "8px",
    padding: "16px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },

}));

const MultipleFilesUpload = ({
  label,
  name,
  value,
  onChange,
  acceptedFileTypes,
  singleFileUpload,
  maxSize = 15 * 1024 * 1024,
  error,
  maxSizeError,
  requirementsText,
  compact = singleFileUpload,
}) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const [fileErrors, setFileErrors] = useState([]);

  // Normalize accept keys (react-dropzone typically expects object like { 'image/*': [] })
  const acceptKeys = acceptedFileTypes ? Object.keys(acceptedFileTypes) : [];

  const areImagesAllowed = acceptKeys.some((type) => type.startsWith("image/"));
  const areVideosAllowed = acceptKeys.some((type) => type.startsWith("video/"));
  const areDocumentsAllowed = acceptKeys.some((type) =>
    type.startsWith("application/")
  );

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      const newErrors = [];
      const newFiles = singleFileUpload
        ? []
        : Array.isArray(value)
        ? [...value]
        : [];

      const selectedFiles = [
        ...acceptedFiles,
        ...fileRejections.map(({ file }) => file),
      ];

      selectedFiles.forEach((selectedFile) => {
        const validationError = acceptedFileTypes
          ? validateImageFile(selectedFile, acceptedFileTypes, maxSize)
          : !Number.isInteger(selectedFile?.size) || selectedFile.size <= 0
          ? "empty"
          : selectedFile.size > maxSize
          ? "tooLarge"
          : null;

        if (validationError) {
          const errorKey = validationError === "unsupported"
            ? "invalid_file_type"
            : "invalid_file_size";
          const translatedError =
            validationError === "tooLarge" && maxSizeError
              ? maxSizeError
              : t(errorKey);
          if (!newErrors.includes(translatedError)) newErrors.push(translatedError);
          return;
        }

        if (fileRejections.some(({ file }) => file === selectedFile)) {
          const translatedError = t("invalid_file_type");
          if (!newErrors.includes(translatedError)) newErrors.push(translatedError);
          return;
        }

        if (singleFileUpload) {
          newFiles[0] = selectedFile;
        } else {
          newFiles.push(selectedFile);
        }
      });

      setFileErrors(newErrors);

      if (selectedFiles.length > 0 && newErrors.length === 0) {
        onChange({ target: { name, value: newFiles } });
      }
    },
    [
      onChange,
      name,
      value,
      acceptedFileTypes,
      singleFileUpload,
      maxSize,
      maxSizeError,
      t,
    ]
  );

  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    onDrop,
    accept: acceptedFileTypes || undefined,
    maxSize,
    noClick: value && value.length > 0,
    onError: () => setFileErrors([t("invalid_file_type")]),
  });

  const addFile = () => {
    if (inputRef?.current) {
      inputRef.current.click();
    }
  };

  const removeFile = (index, event) => {
    event.stopPropagation();
    const newFiles = Array.isArray(value) ? [...value] : [];
    newFiles.splice(index, 1);
    onChange({ target: { name, value: newFiles } });
  };

  const renderPreview = (file, index) => (
    <UploadFilePreview key={index} file={file} compact={compact}
      onRemove={(event) => removeFile(index, event)} />
  );

  const renderFileErrors = (errs) => {
    if (!errs || errs.length === 0) return null;
    return (
      <span className={styles.errorText}>
        {errs.map((error, index) => (
          <React.Fragment key={index}>
            {index > 0 && <br />}
            {error}
          </React.Fragment>
        ))}
      </span>
    );
  };

  const renderButton = () => {
    return (
      <Button
        {...getRootProps()}
        type="button"
        onClick={(event) => { event.stopPropagation(); addFile(); }}
        fullWidth={!compact}
        startIcon={singleFileUpload ? <ThreeSixtyIcon /> : <AddIcon />}
        sx={{ mt: 2, ...(compact ? { alignSelf: "flex-start" } : {}) }}
      >
        {singleFileUpload ? t("replace_file") : t("add_file")}
      </Button>
    );
  };

  const renderUploadArea = () => {
    return (
      <>
        <input {...getInputProps()} />
        <UploadIcon />
        {isDragActive ? (
          <p>
            <span className={styles.text}>{t("drop_files_here")}</span>
          </p>
        ) : (
          <>
            <div>
              <span className={styles.highlightText}>
                {t("click_to_upload")}
              </span>{" "}
              <span className={styles.text}>{t("drag_n_drop")}</span>
            </div>
            {fileErrors.length > 0 ? (
              renderFileErrors(fileErrors)
            ) : (
              <span className={styles.text}>
                {areImagesAllowed && (
                  <strong>{requirementsText || t("image_upload_files")}</strong>
                )}
                {areVideosAllowed && (
                  <>
                    <br />
                    <strong>{t("video_upload_files")}</strong>
                  </>
                )}
                {areDocumentsAllowed && (
                  <>
                    <br />
                    <strong>{t("document_upload_files")}</strong>
                  </>
                )}
              </span>
            )}
          </>
        )}
      </>
    );
  };

  const hasFiles =
    Array.isArray(value) &&
    value.filter((item) => !!item && item !== "" && item !== null).length > 0;

  const cleanFiles = hasFiles ? value.filter(Boolean) : [];

  return (
    <div style={{ width: "100%", minWidth: 0, ...(compact ? { maxWidth: 560 } : {}) }}>
      {label && label?.length > 0 && (
        <span className={styles.label}>{label}</span>
      )}
      <div {...getRootProps()} className={styles.uploadFileContainer}>
        {hasFiles ? (
          <>
            {cleanFiles.length === 1 ? (
              renderPreview(cleanFiles[0], 0)
            ) : (
              <Box sx={{
                display: "grid", gap: 2, width: "100%", maxWidth: 720,
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
                maxHeight: 480, overflowY: "auto",
              }}>
                {cleanFiles.map((file, index) => renderPreview(file, index))}
              </Box>
            )}

            <input {...getInputProps()} style={{ display: "none" }} />
            {renderFileErrors([...fileErrors, ...(error ? [error] : [])])}
            {renderButton()}
          </>
        ) : (
          <>
            {renderUploadArea()}
            {error && renderFileErrors([error])}
          </>
        )}
      </div>
    </div>
  );
};

MultipleFilesUpload.propTypes = {};

export default MultipleFilesUpload;
